import { type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ChartFrame } from '../../../components/chart/chart-frame';
import { FLOW_VAR, RAG_VAR } from '../../../components/chart/rag';
import { coordinationApi, coordinationKeys } from '../../coordination/api';
import { errorMessage } from '../../../lib/error-message';
import { todayPlus } from '../../../lib/date';
import { useOrgIsPersonal } from '../../auth/use-org-is-personal';

/** Bao nhiêu tuần tới được vẽ. Xa hơn thế thì kế hoạch chưa đủ chắc để đọc. */
const WEEKS_AHEAD = 8;

/**
 * Resource histogram theo thời gian — spec 16.3.
 *
 * ## Vì sao không có endpoint báo cáo riêng
 *
 * Dữ liệu này **đã có** từ `APM-504b`: `GET /projects/:id/workload` trả về giờ
 * cam kết và năng lực theo từng ngày, cộng dồn **xuyên mọi dự án** — kể cả dự
 * án người xem không vào được, thứ làm nó có ích. Viết một endpoint báo cáo thứ
 * hai đọc cùng dữ liệu là dựng hai định nghĩa "tải" sẽ lệch nhau ở lần sửa đầu.
 *
 * Ở đây chỉ gộp theo **tuần** thay vì ngày: một histogram 56 cột không đọc được,
 * và câu hỏi thật là "tuần nào ai kín lịch".
 *
 * ⚠️ Không nhận `asOf`, và ghi chú nói rõ vì sao: biểu đồ này nhìn về **tương
 * lai** theo kế hoạch hiện tại. "Tải dự kiến như kế hoạch hồi tháng trước" là
 * một câu hỏi khác, và trả lời được nó là việc của Replay (S9).
 */
export function ResourceHistogram({ projectId }: { projectId: string }): ReactNode {
  const { t } = useTranslation();

  const from = isoDay(0);
  const to = isoDay(WEEKS_AHEAD * 7);

  // Dữ liệu là của tab Nhân sự (`/workload`), mà tab ấy không có ở không gian cá nhân — API trả
  // `PERSONAL_SPACE_RESTRICTED` (APM-PSPACE-b). Một người một mình thì cũng không có tải để so.
  const orgIsPersonal = useOrgIsPersonal();
  const workload = useQuery({
    queryKey: coordinationKeys.teamWorkload(projectId, from, to),
    queryFn: () => coordinationApi.teamWorkload(projectId, from, to),
    enabled: !orgIsPersonal,
  });

  const members = workload.data?.members ?? [];
  const capacity = members[0]?.days[0]?.capacityHours ?? 8;

  // Một cột mỗi người: câu hỏi "ai kín lịch" đọc theo người, không theo ngày.
  const rows = members
    .map((member) => ({
      name: member.displayName,
      committed: Math.round(member.totalCommittedHours * 10) / 10,
      overloadedDays: member.overloadedDayCount,
    }))
    .sort((a, b) => b.committed - a.committed);

  // Năng lực của cả kỳ: số ngày làm việc × giờ mỗi ngày, lấy từ chính dữ liệu
  // trả về thay vì tự nhân — hai chỗ tính khác nhau là hai con số khác nhau.
  const capacityTotal = (members[0]?.days.length ?? 0) * capacity;
  if (orgIsPersonal) return null;

  return (
    <ChartFrame
      title={t('reports.resourceTitle')}
      help="resource"
      subtitle={t('reports.resourceSubtitle')}
      loading={workload.isPending}
      error={workload.isError ? errorMessage(workload.error, t) : null}
      onRetry={() => void workload.refetch()}
      empty={
        workload.data && rows.every((row) => row.committed === 0)
          ? { title: t('reports.resourceEmpty'), description: t('reports.resourceEmptyHelp') }
          : null
      }
      legend={[
        { label: t('reports.resourceCommitted'), color: FLOW_VAR.inProgress },
        { label: t('reports.resourceCapacity'), color: RAG_VAR.unknown },
        { label: t('reports.resourceOverloaded'), color: RAG_VAR.red },
      ]}
    >
      <div className="flex flex-col gap-1">
        <div className="h-44 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="var(--color-border)" />
              <YAxis tick={{ fontSize: 11 }} stroke="var(--color-border)" />
              <Tooltip
                contentStyle={{
                  background: 'var(--color-background)',
                  border: '1px solid var(--color-border)',
                  fontSize: 12,
                }}
              />
              <Bar dataKey="committed" name={t('reports.resourceCommitted')}>
                {rows.map((row) => (
                  <Cell
                    key={row.name}
                    fill={row.overloadedDays > 0 ? RAG_VAR.red : FLOW_VAR.inProgress}
                  />
                ))}
              </Bar>
              {capacityTotal > 0 && (
                <ReferenceLine
                  y={capacityTotal}
                  stroke={RAG_VAR.unknown}
                  strokeDasharray="4 4"
                  label={{ value: t('reports.resourceCapacity'), fontSize: 11 }}
                />
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>

        <p className="text-xs text-muted-foreground">{t('reports.resourceNote')}</p>
      </div>
    </ChartFrame>
  );
}

function isoDay(offsetDays: number): string {
  // Hôm nay của tổ chức, không phải ngày UTC — APM-TZ-i.
  return todayPlus(offsetDays);
}
